import React from "react";
import { NewsLetterOptionType } from "@/types/OptionType";
import { customSanitize } from "../../utils/customSanitize";
import useWindowSize from "../../hooks/useWindowSize";
import styles from "./NewsLetter.module.scss";
import { NewsletterOptionsType as NewsletterTemplateOptionsType } from "@/types/ModuleOptionsType";
import { Modul_Newsletter } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Headline } from "@/components/Headline/Headline";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import { Section } from "@/components/Section/Section";
import Form from "../Form/Form";
import { NewsletterData } from "@/getData/getIndexedNewsletterData";
import { FadeInUp, StaggerFadeInUp } from "@/components/Animation";

export type NewsLetterType = Modul_Newsletter;

interface NewsLetterProps {
  data: NewsLetterType;
  newsletterOptions: NewsLetterOptionType;
  nesletterTemplateOptions: NewsletterTemplateOptionsType;
  friendlyCaptchaApplicationkey: string;
  newsletterData: NewsletterData;
}

export type FormValues = {
  email: string;
  privacy: boolean;
};

const NewsLetter = ({
  data,
  newsletterOptions,
  nesletterTemplateOptions,
  friendlyCaptchaApplicationkey,
  newsletterData,
}: NewsLetterProps) => {
  const {
    section_padding_class,
    section_margin_class,
    background_color_class,
    id,
    row_direction_class,
  } = data.layout;

  const { overline, subline, headline, headline_html_tag, content, image } =
    newsletterOptions;

  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    show_overline_subline,
  } = nesletterTemplateOptions;

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  const { width } = useWindowSize();

  const isMobile = width > 0 && width < 768;

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  const columnClass = {
    "is-row-normal-desktop": {
      text: "is-12-mobile is-7-tablet is-6-desktop is-6-fullhd",
      media: "is-12-mobile is-5-tablet is-6-desktop ",
    },
    "is-row-reverse-desktop": {
      text: "is-12-mobile is-7-tablet is-6-desktop is-6-fullhd ",
      media: "is-12-mobile is-5-tablet is-6-desktop",
    },
  };

  const ContentJSX = (
    <FadeInUp
      data-row-direction={row_direction_class}
      className={`column ${styles["content-col"]}  ${columnClass[row_direction_class || "is-row-normal-desktop"].text} ${show_card_box ? `is-box ` : ""}
          ${show_border_radius ? "has-border-radius" : ""} ${show_box_shadow ? "has-box-shadow" : ""}`}
    >
      <div className={styles["padding-box"]}>
        {headline && (
          <Headline
            headline={headline}
            headline_html_tag={headline_html_tag}
            overline={showOverline ? overline : ""}
            subline={showSubline ? subline : ""}
            disableAnimation
          />
        )}

        {content && (
          <div dangerouslySetInnerHTML={{ __html: customSanitize(content) }} />
        )}

        <Form
          button_type="primary"
          friendlyCaptchaApplicationkey={friendlyCaptchaApplicationkey}
          formData={newsletterData}
        />
      </div>
    </FadeInUp>
  );

  const ImageJSX = (
    <FadeInUp
      className={`${styles["img-col"]} column is-relative ${columnClass[row_direction_class || "is-row-normal-desktop"].media}`}
    >
      {image && (
        <MediaItem
          image_fullhd={image}
          image_desktop={image}
          sizes="(max-width: 1023px) 100vw, 50vw"
          priority={true}
          copyrightPosition={"top"}
        />
      )}
    </FadeInUp>
  );

  return (
    <Section
      id={id}
      className={`newsletter ${styles["newsletter"]} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      containerClassName={styles["newsletter-container"]}
      disableColumns
    >
      <StaggerFadeInUp
        staggerDelay={200}
        className={`${styles["newsletter-box"]} columns is-gapless is-centered is-multiline 
        
        `}
      >
        {isMobile && (
          <>
            {ContentJSX}
            {ImageJSX}
          </>
        )}

        {!isMobile && (
          <>
            {row_direction_class === "is-row-normal-desktop"
              ? ContentJSX
              : ImageJSX}
            {row_direction_class === "is-row-normal-desktop"
              ? ImageJSX
              : ContentJSX}
          </>
        )}
      </StaggerFadeInUp>
    </Section>
  );
};

export default NewsLetter;
